/**
 * Shared Date Parsing Logic
 */
window.parseCountdownDate = window.parseCountdownDate || function (raw) {
  if (!raw) return null;
  const s = raw.trim();

  // Match MM/DD/YYYY
  const mdY = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdY) {
    const month = Number(mdY[1]);
    const day = Number(mdY[2]);
    const year = Number(mdY[3]);
    // Construct date at 23:59:59.999 local time for "End of Day"
    const d = new Date(year, month - 1, day, 23, 59, 59, 999);
    if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
      return d.getTime();
    }
  }

  // Fallback to standard Date parse (ISO or other supported formats)
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d.getTime();
}

/**
 * Legacy Countdown Timer (Class-based)
 * Supports <span class="countdown-timer" data-end-datetime="...">...</span>
 */
if (!window.LegacyCountdownTimer) {
  window.LegacyCountdownTimer = class LegacyCountdownTimer {
    constructor(element) {
      this.element = element;
      this.intervalId = null;
      this.endTime = null;
      this.units = {};

      const endRaw = this.element.getAttribute('data-end-datetime');
      this.endTime = parseCountdownDate(endRaw);

      if (!this.endTime) {
        this.element.classList.add('countdown-timer--invalid');
        return;
      }

      this.units = {
        days: this.element.querySelector('[data-part="days"]'),
        hours: this.element.querySelector('[data-part="hours"]'),
        minutes: this.element.querySelector('[data-part="minutes"]'),
        seconds: this.element.querySelector('[data-part="seconds"]'),
      };

      this.start();
    }

    _pad2(n) {
      return String(n).padStart(2, '0');
    }

    _renderRemaining(ms) {
      const totalSeconds = Math.max(0, Math.floor(ms / 1000));
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const isInline = this.element.classList.contains('countdown-timer--inline');
      const compactDays = (this.element.dataset.compactDays !== 'false') && (isInline || this.element.dataset.compactDays === 'true');

      if (this.units.days) {
        if (compactDays) {
          const compact = days > 99 ? '99+' : this._pad2(days);
          this.units.days.textContent = compact;
          this.units.days.setAttribute('data-full-days', String(days));
        } else {
          this.units.days.textContent = this._pad2(days);
        }
      }
      if (this.units.hours) this.units.hours.textContent = this._pad2(hours);
      if (this.units.minutes) this.units.minutes.textContent = this._pad2(minutes);
      if (this.units.seconds) this.units.seconds.textContent = this._pad2(seconds);
    }

    _tick() {
      const now = Date.now();
      const remaining = this.endTime - now;
      this._renderRemaining(remaining);

      if (remaining <= 0) {
        this.destroy();
        this.element.classList.add('countdown-timer--ended');
        const evt = new CustomEvent('countdown:ended', { bubbles: true, detail: { element: this.element, end: this.endTime } });
        this.element.dispatchEvent(evt);
      }
    }

    start() {
      this._tick();
      this.intervalId = setInterval(() => this._tick(), 1000);
    }

    destroy() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    static initAll(root = document) {
      root.querySelectorAll('.countdown-timer[data-end-datetime]').forEach(element => {
        // Avoid double init: check if already upgraded or tracked
        if (element.dataset.countdownInitialized) return;
        element.dataset.countdownInitialized = 'true';
        new LegacyCountdownTimer(element);
      });
    }
  };
}

/**
 * Modern Countdown Timer (Web Component)
 * Supports <countdown-timer expires-at="...">...</countdown-timer>
 */
if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();
      this.timer = null;
    }

    connectedCallback() {
      // Wait for children
      requestAnimationFrame(() => this.init());
    }

    init() {
      this.units = {
        days: this.querySelector('countdown-timer-flip[type="days"]'),
        hours: this.querySelector('countdown-timer-flip[type="hours"]'),
        minutes: this.querySelector('countdown-timer-flip[type="minutes"]'),
        seconds: this.querySelector('countdown-timer-flip[type="seconds"]'),
      };

      const rawDate = this.getAttribute('data-raw-date'); // Prefer raw from sections
      const expiresAt = this.getAttribute('expires-at');

      this.expiresAt = parseCountdownDate(rawDate) || (expiresAt ? new Date(expiresAt).getTime() : null);
      this.expirationBehavior = this.getAttribute('expiration-behavior') || 'leave';

      if (!this.expiresAt || isNaN(this.expiresAt)) {
        return;
      }

      this.update();
      this.timer = setInterval(this.update.bind(this), 1000);
    }

    disconnectedCallback() {
      if (this.timer) {
        clearInterval(this.timer);
      }
    }

    update() {
      const now = new Date().getTime();
      const distance = this.expiresAt - now;

      if (distance < 0) {
        clearInterval(this.timer);
        this.dispatchEvent(new CustomEvent('countdown:ended', { bubbles: true }));

        if (this.expirationBehavior === 'hide') {
          this.style.display = 'none';
        } else {
          this.updateDisplay(0, 0, 0, 0);
        }
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      this.updateDisplay(days, hours, minutes, seconds);
    }

    updateDisplay(days, hours, minutes, seconds) {
      if (this.units.days) this.units.days.update(days);
      if (this.units.hours) this.units.hours.update(hours);
      if (this.units.minutes) this.units.minutes.update(minutes);
      if (this.units.seconds) this.units.seconds.update(seconds);
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
}

if (!customElements.get('countdown-timer-flip')) {
  class CountdownTimerFlip extends HTMLElement {
    constructor() { super(); }
    update(value) {
      const formatted = value < 10 ? `0${value}` : value;
      if (this.textContent !== String(formatted)) {
        this.textContent = formatted;
      }
    }
  }
  customElements.define('countdown-timer-flip', CountdownTimerFlip);
}

// Initialize Legacy Timers
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => LegacyCountdownTimer.initAll());
} else {
  LegacyCountdownTimer.initAll();
}

// Re-init on Shopify events
document.addEventListener('shopify:section:load', (e) => {
  const container = e && e.target ? e.target : document;
  LegacyCountdownTimer.initAll(container);
});

// Re-init on Cart Drawer refresh
document.addEventListener('cart-drawer:refreshed', (e) => {
  const container = e && e.target ? e.target : document;
  LegacyCountdownTimer.initAll(container);
});
